<template>
  <div class="statistics-container">
    <!-- 核心统计区域 -->
    <el-row :gutter="20" class="stats-wrapper">
      <!-- 1. 订单状态统计 -->
      <el-col :span="24" class="stats-card-wrapper">
        <el-card shadow="hover" class="stats-card">
          <div slot="header" class="card-header">
            <h2 class="card-title">订单状态</h2>
          </div>
          <el-row :gutter="15" class="order-stats-row">
            <el-col
              :span="6"
              v-for="(item, key) in orderStats"
              :key="key"
              class="order-stat-item"
            >
              <div class="stat-label">{{ key }}</div>
              <div class="stat-value" @click="navigateToOrderManage(key)">
                {{ item }}
              </div>
            </el-col>
          </el-row>
        </el-card>
      </el-col>

      <!-- 2. 营业额统计（新增：自定义日期查询） -->
      <el-col :span="24" class="stats-card-wrapper">
        <el-card shadow="hover" class="stats-card">
          <div slot="header" class="card-header">
            <h2 class="card-title">营业额</h2>
            <div class="time-filter">
              <!-- 时间筛选按钮组 -->
              <el-button
                v-for="(time, key) in timeOptions"
                :key="key"
                :type="activeTimeKey === key ? 'primary' : 'default'"
                @click="handleTimeChange(key)"
              >
                {{ time.label }}
              </el-button>
              <!-- 自定义日期选择器（仅选中"自定义日期"时显示） -->
              <el-date-picker
                v-if="activeTimeKey === 'custom'"
                v-model="dateRange"
                type="daterange"
                range-separator="至"
                start-placeholder="开始日期"
                end-placeholder="结束日期"
                value-format="yyyy-MM-dd"
                @change="handleDateRangeChange"
                class="custom-date-picker"
              ></el-date-picker>
            </div>
          </div>
          <div class="turnover-value">
            总营业额：<span class="amount">¥{{ turnover.toFixed(2) }}</span>
          </div>
        </el-card>
      </el-col>

      <!-- 3. 消费者数据统计（删除：查询功能） -->
      <el-col :span="24" class="stats-card-wrapper">
        <el-card shadow="hover" class="stats-card">
          <div slot="header" class="card-header">
            <h2 class="card-title">消费者数据</h2>
            <!-- 已删除：消费者查询表单 -->
          </div>
          <el-row :gutter="15" class="consumer-stats-row">
            <el-col :span="8" class="consumer-stat-item">
              <div class="stat-label">今日新增消费者</div>
              <div
                class="stat-value"
                @click="navigateToConsumerManage('today')"
              >
                {{ consumerStats.todayNew }} 人
              </div>
            </el-col>
            <el-col :span="8" class="consumer-stat-item">
              <div class="stat-label">本月新增消费者</div>
              <div
                class="stat-value"
                @click="navigateToConsumerManage('month')"
              >
                {{ consumerStats.monthNew }} 人
              </div>
            </el-col>
            <el-col :span="8" class="consumer-stat-item">
              <div class="stat-label">总消费者人数</div>
              <div
                class="stat-value"
                @click="navigateToConsumerManage('total')"
              >
                {{ consumerStats.total }} 人
              </div>
            </el-col>
          </el-row>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script>
export default {
  name: "Statistics",
  data() {
    return {
      // 1. 订单状态统计数据（待发货、待收货、完成、已取消）
      orderStats: {
        待发货: 0,
        待收货: 0,
        完成: 0,
        已取消: 0,
      },

      // 2. 营业额相关数据
      turnover: 0, // 营业额数值
      dateRange: [], // 自定义日期范围：[开始日期, 结束日期]（格式：yyyy-MM-dd）
      // 时间筛选配置（新增"自定义日期"选项）
      timeOptions: {
        today: { label: "今日", type: "today" },
        sevenDays: { label: "近7日", type: "sevenDays" },
        thirtyDays: { label: "近一个月", type: "thirtyDays" },
        year: { label: "近一年", type: "year" },
        custom: { label: "自定义日期", type: "custom" }, // 新增自定义选项
      },
      activeTimeKey: "today", // 当前激活的时间筛选

      // 3. 消费者统计数据（已删除：consumerSearchForm 查询表单）
      consumerStats: {
        todayNew: 0, // 今日新增
        monthNew: 0, // 本月新增
        total: 0, // 总人数
      },
    };
  },
  mounted() {
    // 页面初始化：加载所有统计数据
    this.fetchAllStats();
  },
  methods: {
    /**
     * 统一获取所有统计数据（订单+营业额+消费者）
     */
    async fetchAllStats() {
      try {
        const token = localStorage.getItem("token");
        // 未登录：跳转至商家登录页
        if (!token) {
          this.$message.warning("请先登录");
          this.$router.push("/MerchantLogin");
          return;
        }
        // 并行加载三类数据（提升性能）
        await Promise.all([
          this.fetchOrderStats(token),
          this.fetchTurnover(token, this.activeTimeKey, this.dateRange),
          this.fetchConsumerStats(token),
        ]);
      } catch (error) {
        console.error("获取统计数据异常：", error);
        this.$message.error(error.message || "获取数据失败，请稍后重试");
      }
    },

    /**
     * 1. 获取订单状态统计数据
     * @param {string} token - 商家登录令牌
     */
    async fetchOrderStats(token) {
      const response = await this.$http.get(
        "/api/merchant/statistics/order-stats",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.code === 0) {
        this.orderStats = response.data.data;
      } else {
        throw new Error(response.data.message || "获取订单状态统计失败");
      }
    },

    /**
     * 2. 获取营业额统计数据（支持自定义日期）
     * @param {string} token - 商家登录令牌
     * @param {string} timeKey - 时间筛选key（today/sevenDays/.../custom）
     * @param {array} customDate - 自定义日期范围（仅timeKey=custom时生效）
     */
    async fetchTurnover(token, timeKey, customDate = []) {
      const params = { timeKey };
      // 自定义日期：传递开始/结束日期参数
      if (timeKey === "custom" && customDate.length === 2) {
        params.startDate = customDate[0];
        params.endDate = customDate[1];
      }
      const response = await this.$http.get(
        "/api/merchant/statistics/turnover",
        {
          params,
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.code === 0) {
        this.turnover = response.data.data;
      } else {
        throw new Error(response.data.message || "获取营业额统计失败");
      }
    },

    /**
     * 3. 获取消费者统计数据
     * @param {string} token - 商家登录令牌
     */
    async fetchConsumerStats(token) {
      const response = await this.$http.get(
        "/api/merchant/statistics/consumer-stats",
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      if (response.data.code === 0) {
        this.consumerStats = response.data.data;
      } else {
        throw new Error(response.data.message || "获取消费者统计失败");
      }
    },

    /**
     * 时间筛选切换（含自定义日期逻辑）
     * @param {string} timeKey - 选中的时间key
     */
    handleTimeChange(timeKey) {
      this.activeTimeKey = timeKey;
      const token = localStorage.getItem("token");

      if (timeKey === "custom") {
        // 切换到自定义日期：有选择范围则查询，无则提示
        if (this.dateRange.length === 2) {
          this.fetchTurnover(token, timeKey, this.dateRange);
        } else {
          this.$message.warning("请选择完整的日期范围（开始日期至结束日期）");
        }
      } else {
        // 切换到非自定义：清空日期选择器，重新查询
        this.dateRange = [];
        this.fetchTurnover(token, timeKey);
      }
    },

    /**
     * 自定义日期范围变化时触发查询
     */
    handleDateRangeChange() {
      if (this.dateRange.length === 2) {
        this.fetchTurnover(
          localStorage.getItem("token"),
          "custom",
          this.dateRange
        );
      } else {
        this.$message.warning("请选择完整的日期范围");
      }
    },

    /**
     * 跳转至订单管理页（核心修改：添加中文→英文状态映射）
     * @param {string} chineseStatus - 中文订单状态（如“待发货”“待收货”）
     */
    navigateToOrderManage(chineseStatus) {
      // 1. 建立中文状态与 OrderManage.vue 所需英文状态的精准映射（必须与OrderManage的status值一致）
      const statusMap = {
        待发货: "pending", // 对应OrderManage的“待发货”状态
        待收货: "shipped", // 对应OrderManage的“待收货”状态
        完成: "completed", // 对应OrderManage的“完成”状态
        已取消: "canceled", // 对应OrderManage的“已取消”状态
      };
      // 2. 根据中文状态获取目标英文状态（若未匹配到，默认待发货）
      const targetStatus = statusMap[chineseStatus] || "pending";
      // 3. 跳转到订单管理页，通过query传递状态参数（移除encodeURIComponent，避免解析异常）
      this.$router.push({
        name: "OrderManage", // 确保路由名称与OrderManage.vue的路由配置一致
        query: { status: targetStatus }, // 直接传递英文状态，无需编码
      });
    },

    /**
     * 跳转至消费者管理页（携带时间筛选参数）
     * @param {string} type - 时间类型（today/month/total）
     */
    navigateToConsumerManage(type) {
      const query = {};
      const now = new Date();

      switch (type) {
        case "today":
          // 今日：00:00:00 至 23:59:59
          const todayStart = new Date(now.setHours(0, 0, 0, 0)).toISOString();
          const todayEnd = new Date(
            now.setHours(23, 59, 59, 999)
          ).toISOString();
          query.startTime = todayStart;
          query.endTime = todayEnd;
          break;
        case "month":
          // 本月：1号00:00:00 至 月末23:59:59
          const monthStart = new Date(
            now.getFullYear(),
            now.getMonth(),
            1,
            0,
            0,
            0
          ).toISOString();
          const monthEnd = new Date(
            now.getFullYear(),
            now.getMonth() + 1,
            0,
            23,
            59,
            59
          ).toISOString();
          query.startTime = monthStart;
          query.endTime = monthEnd;
          break;
        case "total":
          // 总人数：无时间筛选
          break;
      }

      this.$router.push({
        name: "ConsumerManage",
        query,
      });
    },
  },
};
</script>

<style scoped>
/* 容器基础样式 */
.statistics-container {
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  min-height: calc(100vh - 64px);
}

/* 头部样式（仅保留点击区域） */
.page-header {
  height: 40px;
  margin-bottom: 20px;
  cursor: pointer;
}

/* 统计卡片通用样式 */
.stats-wrapper {
  margin-bottom: 20px;
}
.stats-card-wrapper {
  margin-bottom: 20px;
}
.stats-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}
.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px 20px;
  border-bottom: 1px solid #f2f2f2;
}
.card-title {
  font-size: 16px;
  font-weight: 600;
  color: #333;
  margin: 0;
}

/* 时间筛选区域样式（含自定义日期选择器） */
.time-filter {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap; /* 适配小屏幕，避免溢出 */
}
.custom-date-picker {
  width: 280px;
  margin-left: 12px;
}

/* 订单状态统计样式 */
.order-stats-row,
.consumer-stats-row {
  padding: 20px;
}
.order-stat-item,
.consumer-stat-item {
  text-align: center;
  padding: 16px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

/* 统计项通用样式 */
.stat-label {
  font-size: 14px;
  color: #666;
  margin-bottom: 8px;
}
.stat-value {
  font-size: 24px;
  font-weight: 700;
  color: #333;
  cursor: pointer;
  transition: color 0.3s;
}
.stat-value:hover {
  color: #409eff;
}

/* 营业额数值样式 */
.turnover-value {
  padding: 20px;
  font-size: 16px;
  color: #333;
}
.amount {
  font-size: 24px;
  font-weight: 700;
  color: #f56c6c;
  margin-left: 8px;
}
</style>